import type { AnalyticsSummary, BotEvent, HealthResponse } from '@bale-bot/types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import SectionCard from '../components/SectionCard';
import StatTile from '../components/StatTile';
import { api, describeError } from '../services/api';

const STATUS = {
  good: { color: '#22c55e', icon: <CheckCircleIcon fontSize="small" /> },
  warning: { color: '#f59e0b', icon: <WarningAmberIcon fontSize="small" /> },
};

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

export default function DashboardPage() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [events, setEvents] = useState<BotEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([api.health(), api.getAnalytics(7), api.getRecentEvents(10)])
      .then(([healthData, summaryData, eventsPage]) => {
        if (!active) return;
        setHealth(healthData);
        setSummary(summaryData);
        setEvents(eventsPage.items);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeError(cause));
      });
    return () => {
      active = false;
    };
  }, []);

  const botStatus = health?.botConfigured ? STATUS.good : STATUS.warning;

  return (
    <Stack spacing={3} className="stagger">
      <PageHeader
        title="Dashboard"
        description="Service health and the last seven days of bot activity."
      />

      {error && <Alert severity="error">{error}</Alert>}

      {/* Bento: health strip + stats */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(240px, 1fr) repeat(2, 1fr)',
            lg: 'minmax(260px, 1.1fr) repeat(4, 1fr)',
          },
        }}
      >
        {health && (
          <SectionCard
            title="System"
            sx={{
              gridColumn: { xs: '1', md: '1', lg: '1' },
              gridRow: { lg: 'span 1' },
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha(botStatus.color, 0.15),
                    color: botStatus.color,
                  }}
                >
                  {botStatus.icon}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {health.botConfigured ? 'Bot online' : 'Token missing'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {health.botConfigured
                      ? 'Ready to send and receive'
                      : 'Set BALE_BOT_TOKEN'}
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={1}>
                <MetaRow
                  icon={<StorageIcon fontSize="inherit" />}
                  label="Database"
                  value={health.drivers.database}
                />
                <MetaRow
                  icon={<MemoryIcon fontSize="inherit" />}
                  label="Sessions"
                  value={health.drivers.session}
                />
                <MetaRow
                  icon={<TimerOutlinedIcon fontSize="inherit" />}
                  label="Uptime"
                  value={formatUptime(health.uptimeSeconds)}
                />
              </Stack>
            </Stack>
          </SectionCard>
        )}

        {summary && (
          <>
            <StatTile label="Events" value={summary.totalEvents} caption="last 7 days" />
            <StatTile label="Unique users" value={summary.uniqueUsers} caption="last 7 days" />
            <StatTile label="To manager" value={summary.routedToAccountManager} caption="routed" />
            <StatTile label="Live chat" value={summary.routedToLiveChat} caption="routed" />
          </>
        )}
      </Box>

      <SectionCard
        title="Recent events"
        description="Latest activity from the bot event log."
      >
        {events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing logged yet. Send <code>/start</code> to the bot to generate the first event.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto', mx: { xs: -1, sm: 0 } }}>
            <Table size="small" sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event, index) => (
                  <TableRow
                    key={event.id ?? index}
                    hover
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={event.eventType}
                        variant="outlined"
                        sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {event.userId}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', maxWidth: 280 }}>
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          overflowWrap: 'anywhere',
                          fontSize: '0.8rem',
                        }}
                      >
                        {event.payload ? JSON.stringify(event.payload) : '—'}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </SectionCard>
    </Stack>
  );
}

function MetaRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
        px: 1.25,
        borderRadius: 2,
        bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === 'dark' ? 0.04 : 0.03),
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary' }}>
        <Box sx={{ display: 'flex', fontSize: 16 }}>{icon}</Box>
        <Typography variant="caption">{label}</Typography>
      </Stack>
      <Chip size="small" label={value} variant="outlined" />
    </Stack>
  );
}
